import React, { useEffect, useState, useRef } from "react"
import axios from "axios"
import API_BASE_URL from "config/api"
import { fetchProductSalesSummary } from "api/sales"
import { useInterval } from "contexts/IntervalContext"
import InventoryandComponentsTable from "./InventoryandComponentsTable"
import PredictionExplanation from "./PredictionExplanation"
// import Graph from "./SalesGraph"
import ProductSalesGraph from "./ProductSalesGraph";
import ForecastSummary from "../../default/components/ForecastSummary";
import StatCard from "../../../../components/card/StatCard";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "components/ui/card"
import { Button } from "components/ui/button"
import { Skeleton } from "components/ui/skeleton"
import { cn } from "lib/utils"
import { ArrowUpRight, ArrowDownRight } from "lucide-react";


function ActionRecommendation({ actions }) {
  if (!actions || actions.length === 0) return null

  return (
    <Card className="border-yellow-500/30 bg-yellow-500/5">
      <CardHeader>
        <CardTitle>Recommended Actions</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {actions.map((action, idx) => (
          <div
            key={idx}
            className="p-3 rounded-lg bg-neutral-900/60 border border-neutral-800"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-white">
                  {action.title}
                </p>
                <p className="text-sm text-neutral-400 mt-1">
                  {action.reason}
                </p>
              </div>

              <span
                className={`text-xs px-2 py-1 rounded ${
                  action.confidence === "high"
                    ? "bg-green-500/20 text-green-400"
                    : action.confidence === "medium"
                    ? "bg-yellow-500/20 text-yellow-400"
                    : "bg-neutral-500/20 text-neutral-300"
                }`}
              >
                {action.confidence}
              </span>
            </div>

            {action.expected_profit_impact > 0 && (
              <p className="text-sm text-yellow-400 mt-2">
                Estimated impact: +${action.expected_profit_impact}
              </p>
            )}
            
          </div>
          
        ))}
        
      </CardContent>
    </Card>
  )
}

export default function ProductDetails({ itemId, isOpen }) {
  const [productMetrics, setProductMetrics] = useState(null)
  const [user, setUser] = useState(null)
  const [salesData, setSalesData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState("amount")
  const { interval } = useInterval()
  const fetchInProgress = useRef(false)

const demandIndex = salesData?.forecast_metrics?.demand_index;
const demandStatus = salesData?.forecast_metrics?.demand_status;

// badge 판단 (StatCard는 판단 ❌, 여기서 판단 ✅)
const demandBadge =
  demandStatus === "high"
    ? {
        text: "HIGH",
        tone: "positive",
        icon: <ArrowUpRight className="w-3 h-3" />,
      }
    : demandStatus === "low"
    ? {
        text: "LOW",
        tone: "negative",
        icon: <ArrowDownRight className="w-3 h-3" />,
      }
    : {
        text: "STABLE",
        tone: "neutral",
        icon: null,
      };
  // ✅ 사용자 정보
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("access_token")
        const res = await axios.get(`${API_BASE_URL}/api/users/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setUser(res.data)
      } catch (err) {
        console.error("Failed to fetch user info:", err)
      }
    }
    fetchUser()
  }, [])

  // ✅ 그래프 데이터
  useEffect(() => {
    const loadSalesData = async () => {
      const token = localStorage.getItem("access_token")
      if (!itemId || !user?.id || !token) return
      if (fetchInProgress.current) return
      fetchInProgress.current = true
      setIsLoading(true)
      try {
        const data = await fetchProductSalesSummary(itemId, interval,  user.membership_tier)
          console.log("🟢 [ProductDetail] salesData:", data);

        setSalesData(data)
      } catch (err) {
        console.error("Failed to load sales summary:", err)
      } finally {
        setIsLoading(false)
        fetchInProgress.current = false
      }
    }

    loadSalesData()
  }, [itemId, user?.id, interval])

  
  useEffect(() => {
  if (isOpen) {
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event("resize"))
    }, 500) // 250 → 500으로 늘리면 애니메이션 끝난 뒤에 트리거됨
    return () => clearTimeout(timeout)
  }
}, [isOpen])
  const tier = user?.membership_tier || "free"

  return (
    <div className="space-y-6 w-full">

    
     {/* 🔥 1. Action Recommendation (NEW) */}
        {salesData?.forecast_summary?.action_recommendations && (
      <ActionRecommendation
        actions={salesData.forecast_summary.action_recommendations}
      />
    )}
     
{salesData?.forecast_metrics && (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">

    <StatCard
      title="Demand"
      value={demandStatus?.toUpperCase() ?? "—"}
      badge={demandBadge}
      trend={
        salesData?.forecast_summary?.key_drivers?.length
          ? `Driven by ${salesData.forecast_summary.key_drivers[0]}`
          : "No dominant demand driver"
      }
      description={
        demandIndex
          ? `Index ${Math.round(demandIndex)} (baseline = 100)`
          : "Baseline demand"
      }
    />

    <StatCard
      title="Predicted Revenue"
      value={`$${salesData.forecast_metrics.total_predicted_revenue}`}
      change=""
      trend="Expected revenue this period"
      description="Based on forecasted units × price"
    />

    <StatCard
      title="Predicted Cost"
      value={`$${salesData.forecast_metrics.total_predicted_cost}`}
      change=""
      trend="Projected product cost"
      description="Based on unit cost × forecast volume"
    />

    <StatCard
      title="Gross Profit"
      value={`$${salesData.forecast_metrics.total_gross_profit}`}
      change=""
      trend="Profit after cost"
      description="Revenue − Cost"
    />

    <StatCard
      title="Stockout Risk"
      value={salesData.forecast_metrics.stockout_risk.toUpperCase()}
      change=""
      trend={
        salesData.forecast_metrics.stockout_risk === "high"
          ? "⚠ Immediate attention required"
          : salesData.forecast_metrics.stockout_risk === "medium"
          ? "Monitor closely"
          : "Stable inventory levels"
      }
      description="Based on forecast vs current stock"
    />

    <StatCard
      title="Recommended Restock"
      value={`${salesData.forecast_metrics.recommended_restock_qty} units`}
      change=""
      trend="Suggested restock quantity"
      description="To meet expected demand"
    />

  </div>
)}
      {/* === 그래프 + 요약 === */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 max-w-full overflow-x-hidden">
        {/* Sales Graph */}
        <Card className="bg-muted/40 border-border/60 text-foreground">
          <CardHeader className="flex justify-between items-center">
            <CardTitle>Sales Graph</CardTitle>
            
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Skeleton className="h-6 w-40 mb-3" />
                <p>Loading sales data...</p>
              </div>
            ) : (
              <ProductSalesGraph
                salesData={salesData}
                isLoading={!salesData}
                interval={interval}
                height={280}
              />
            )}
          </CardContent>
        </Card>

        {/* Forecast Summary */}
        
        {salesData?.forecast_summary && (
          <ForecastSummary
            summary={salesData?.forecast_summary?.summary_text ?? null}
            growthRate={salesData?.forecast_summary?.growth_summary ?? null}
            topDrivers={salesData?.forecast_summary?.key_drivers ?? []}
            actionPlan={salesData?.forecast_summary?.action_plan ?? []}
            inventoryNotes={salesData?.forecast_summary?.inventory_notes ?? null}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* === 인벤토리 테이블 === */}
      <Card className="bg-muted/40 border-border/60 text-foreground">
        <InventoryandComponentsTable itemid={itemId} />
      </Card>
     
    </div>
  )
}